import React, { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';

// Livetest-Punkt 16: ChunkLoadError auf Lazy-Routen. Tritt auf, wenn ein Tab mit
// altem index.html nach einem Deploy einen Routen-Chunk nachladen will, dessen
// Hash-Dateiname nicht mehr existiert (404). Ohne errorElement zeigt React Router
// seine rohe Entwickler-Fehlerseite; die äußere ErrorBoundary greift nicht, weil
// der Data-Router Routen-Fehler intern abfängt.
//
// Pure Funktion, aus Jest testbar. Deckt die Varianten aller Browser ab:
//   Chrome/webpack: name 'ChunkLoadError' + 'Loading chunk N failed'
//   Firefox/Safari: 'error loading dynamically imported module' / 'Importing a module script failed'
export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = String(error.message || error);
  return /loading chunk [\d]+ failed/i.test(msg)
    || /failed to fetch dynamically imported module/i.test(msg)
    || /error loading dynamically imported module/i.test(msg)
    || /importing a module script failed/i.test(msg);
}

// Schlüssel + Fenster für den Reload-Schutz: nach einem selbstausgelösten Reload
// wird derselbe ChunkLoadError NICHT erneut zum Reload führen (Endlosschleife),
// sondern zeigt die manuelle Fehlerseite. Nach RELOAD_WINDOW_MS gilt ein neuer
// Chunk-Fehler wieder als frisch (echter neuer Deploy) und darf erneut reloaden.
const RELOAD_KEY = '_ts_chunk_reload_at';
const RELOAD_WINDOW_MS = 15000;

export function shouldAutoReload(now = Date.now(), storage = window.sessionStorage) {
  try {
    const last = Number(storage.getItem(RELOAD_KEY) || 0);
    if (now - last < RELOAD_WINDOW_MS) return false; // gerade erst reloaded → nicht loopen
    storage.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    return false; // ohne sessionStorage lieber die Fehlerseite als ein Loop-Risiko
  }
}

export default function RouteError() {
  const error = useRouteError();
  const chunk = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (chunk && shouldAutoReload()) {
      setReloading(true);
      window.location.reload();
    }
  }, [chunk]);

  // Während des Selbstheilungs-Reloads dieselbe Optik wie der Chunk-Ladefallback.
  if (chunk && reloading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading__spinner" />
      </div>
    );
  }

  const title = chunk ? 'Neue Version verfügbar' : 'Etwas ist schiefgelaufen';
  const text = chunk
    ? 'Diese Seite wurde inzwischen aktualisiert. Bitte lade die App neu, um die neueste Version zu laden.'
    : 'Beim Laden der Seite ist ein Fehler aufgetreten. Ein Neuladen behebt das meist.';

  return (
    <div className="route-error">
      <div className="route-error__card">
        <div className="route-error__icon">{chunk ? '🔄' : '⚠️'}</div>
        <h1 className="route-error__title">{title}</h1>
        <p className="route-error__text">{text}</p>
        <button className="route-error__btn" onClick={() => window.location.reload()}>
          App neu laden
        </button>
      </div>
    </div>
  );
}
